import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmerAnim = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.blue11};
`;

export const LoadingSpinner = styled.div`
  width: 44px;
  height: 44px;
  border: 3px solid ${({ theme }) => theme.colors.blue3};
  border-top-color: ${({ theme }) => theme.colors.blue8};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const LoadingText = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.blue11};
  letter-spacing: 0.05em;
`;

export const SkeletonCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.blue2};
  border: 1px solid ${({ theme }) => theme.colors.blue3};
`;

export const SkeletonThumb = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.blue2} 25%,
    ${({ theme }) => theme.colors.blue3} 50%,
    ${({ theme }) => theme.colors.blue2} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnim} 1.5s infinite;
`;

export const SkeletonLine = styled.div<{ width?: string }>`
  height: 14px;
  border-radius: 4px;
  margin: 0.5rem 1rem;
  width: ${({ width }) => width || '80%'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.blue2} 25%,
    ${({ theme }) => theme.colors.blue3} 50%,
    ${({ theme }) => theme.colors.blue2} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnim} 1.5s infinite;
`;

export const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.blue11};
  animation: ${bounce} 2s infinite;
  cursor: pointer;

  i {
    font-size: 2rem;
  }

  span {
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

export const HeroVideo = styled.div`
  position: absolute;
  inset: -6px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background:
      linear-gradient(to bottom, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0) 28%),
      linear-gradient(to top, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0) 34%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    pointer-events: none;
    background:
      radial-gradient(circle at center, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0.48) 78%),
      rgba(0, 0, 0, 0.42);
  }

  iframe {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1.2);
    width: 177.77777778vh;
    height: 56.25vw;
    min-width: 100%;
    min-height: 100%;
    pointer-events: none;
    opacity: 0.6;

    @media (max-width: 768px) {
      width: 300%;
      height: 100%;
    }
  }
`;

export const VideoModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

export const ModalContent = styled.div`
  width: 100%;
  max-width: 1200px;
  position: relative;
  aspect-ratio: 16/9;

  @media (max-width: 768px) {
    max-width: 100%;
    height: auto;
  }
`;

export const ResponsiveIframe = styled.iframe`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: -3rem;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  z-index: 1001;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    top: -2.5rem;
    right: 0.5rem;
    font-size: 1.5rem;
  }
`;

export const Categories = styled.div`
  background: ${({ theme }) => theme.colors.blue2};
  position: sticky;
  margin-top: 16px;
  top: 0;
  z-index: 10;
  border-radius: 12px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    background: ${({ theme }) => theme.colors.blue1};
    margin: 0;
    border-radius: 0;
    padding: 0.5rem 0;
  }
`;

export const CategoriesScroll = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 1rem 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-radius: 6px;
  width: 100%;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.25rem 1rem;
    gap: 0.5rem;
  }
`;

export const CategoryButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  background: ${({ theme, active }) => active ? theme.colors.blue4 : theme.colors.blue3};
  color: ${({ theme }) => theme.colors.blue9};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  
  &:hover {
    background: ${({ theme }) => theme.colors.blue5};
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    border-radius: 4px;
  }
`;

export const Hero = styled.div`
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.blue1};

  @media (max-width: 768px) {
    height: 45vh;
    min-height: 240px;
  }

  @media (max-width: 480px) {
    height: 38vh;
    min-height: 200px;
  }
`;

export const HeroContent = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  --hero-copy-shift: 2.25rem;
  text-align: center;
  padding: clamp(4.25rem, 12vh, 7.25rem) clamp(1rem, 4vh, 2rem) clamp(1rem, 4vh, 2rem);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  
  @media (max-width: 768px) {
    --hero-copy-shift: 1rem;
    padding: clamp(1.5rem, 6vh, 2.5rem) 1rem 1rem;
  }

  @media (max-width: 480px) {
    --hero-copy-shift: 0.5rem;
    padding-top: 1.25rem;
  }
`;

export const HeroTitle = styled.h1`
  font-size: 3.5rem;
  color: ${({ theme }) => theme.colors.blue9};
  font-weight: 700;
  margin: 0 0 0.75rem;
  position: relative;
  top: var(--hero-copy-shift);

  @media (max-width: 768px) {
    font-size: 1.65rem;
    margin-bottom: 0.5rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.blue11};
  margin-bottom: 2rem;
  line-height: 1.6;
  position: relative;
  top: var(--hero-copy-shift);
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.25;
    margin-bottom: 0;
  }
`;

export const AnalyticsPanel = styled.div`
  width: min(100%, 420px);
  min-height: 104px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  padding: 1rem 1.1rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.blue4};
  background: rgba(15, 17, 19, 0.9);
  backdrop-filter: blur(14px);
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.9rem;
  align-items: center;
  text-align: left;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.42);

  @media (max-width: 768px) {
    width: min(100% - 2rem, 360px);
    min-height: 90px;
    padding: 0.8rem;
    gap: 0.7rem;
  }

  @media (max-width: 480px) {
    width: calc(100% - 1.5rem);
    min-height: 78px;
    padding: 0.7rem;
  }
`;

export const AnalyticsIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.blue3};
  color: ${({ theme }) => theme.colors.blue9};
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 1.45rem;
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;

    i {
      font-size: 1.2rem;
    }
  }
`;

export const AnalyticsCopy = styled.div`
  min-width: 0;
`;

export const AnalyticsValue = styled.div`
  color: ${({ theme }) => theme.colors.blue9};
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.2rem;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

export const AnalyticsLabel = styled.div`
  color: ${({ theme }) => theme.colors.blue9};
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.2;
`;

export const AnalyticsDetail = styled.div`
  color: ${({ theme }) => theme.colors.blue11};
  font-size: 0.72rem;
  line-height: 1.3;
  margin-top: 0.25rem;
`;

export const AnalyticsDots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const AnalyticsDot = styled.span<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${({ theme, $active }) => $active ? theme.colors.blue9 : theme.colors.blue5};
  opacity: ${({ $active }) => $active ? 1 : 0.65};
  transition: background 0.2s ease, opacity 0.2s ease;
`;

export const AnalyticsSkeleton = styled.div`
  grid-column: 1 / -1;
  height: 56px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.blue2} 25%,
    ${({ theme }) => theme.colors.blue3} 50%,
    ${({ theme }) => theme.colors.blue2} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnim} 1.5s infinite;
`;

export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;
`;

export const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.blue9};
  font-size: clamp(1.8rem, 4vw, 2.75rem);
  line-height: 1.05;
  margin: 0;
  padding: 1.5rem 1rem 0;

  @media (max-width: 768px) {
    font-size: 1.55rem;
    padding: 1rem 0.75rem 0;
  }
`;

export const VideoGrid = styled.div`
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    padding: 0.5rem;
  }
`;

// First, declare VideoOverlay
export const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;

  i {
    font-size: 3rem;
    color: white;
    transform: scale(0.9);
    transition: transform 0.2s ease;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);

    i { transform: scale(1); }
  }

  /* Always show play icon on touch devices */
  @media (hover: none) {
    opacity: 1;
    background: rgba(0, 0, 0, 0.3);

    i { transform: scale(1); }
  }
`;

// Then, declare VideoCard that references VideoOverlay
export const VideoCard = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.blue2};
  border: 1px solid ${({ theme }) => theme.colors.blue3};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    ${VideoOverlay} {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    border-radius: 8px;
    border-width: 0.5px;
  }
`;

export const VideoInfo = styled.div`
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

export const VideoTitle = styled.h3`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.blue9};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
    line-height: 1.2;
  }
`;

export const VideoDescription = styled.p`
  color: ${({ theme }) => theme.colors.blue11};
  font-size: 0.9rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const MobileFilter = styled.div`
  position: relative;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.blue1};
  z-index: 20;
  margin-top: 6px;
`;

export const FilterButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.blue3};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.blue11};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.blue4};
  }

  i {
    font-size: 1.2rem;
  }
`;

export const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0.75rem;
  right: 0.75rem;
  background: ${({ theme }) => theme.colors.blue2};
  border: 1px solid ${({ theme }) => theme.colors.blue3};
  border-radius: 8px;
  margin-top: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export const FilterOption = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  background: ${({ theme, active }) => active ? theme.colors.blue4 : 'transparent'};
  border: none;
  color: ${({ theme }) => theme.colors.blue11};
  text-align: left;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.blue3};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.blue3};
  }
`;

export const VideoThumbnail = styled.div<{ bgImage: string }>`
  width: 100%;
  aspect-ratio: 16/9;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
`;
